import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate , useLocation } from 'react-router-dom';
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import { useAuthContext } from '../context';
import toast from 'react-hot-toast';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google'; // For web
import { Capacitor } from '@capacitor/core';
import { InAppBrowser } from "@awesome-cordova-plugins/in-app-browser";
 
const ForgotPasswordModal = ({ isVisible, onClose }) => {
  const { OTPset } = useAuthContext();
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [useMobile, setUseMobile] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false); 
  const navigate = useNavigate();

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  const handleSendOTP = async () => {
      setSendingOTP(true);
      try {
          const result = useMobile
              ? await OTPset({ mobile })
              : await OTPset({ email });

          if (result) {
              toast.success(result.message || "OTP sent successfully");
              onClose();
              navigate('/forgot-otp', {
                  state: {
                      Forgotmobile: useMobile ? mobile : null,
                      Forgotemail: useMobile ? null : email,
                      purpose: 'passwordChange',
                  },
              }); 
              scrollToTop();
          }
      } catch (error) {
          toast.error("Failed to send OTP");
          setSendingOTP(false); // Re-enable on error
      }
  };

  if (!isVisible) return null;

  return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4">Forgot Password</h2>

              {useMobile ? (
                  <input
                      type="text"
                      className="px-4 w-full border-2 py-2 rounded-md text-sm outline-none mb-4"
                      placeholder="Enter your registered mobile number"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                  />
              ) : (
                  <input
                      type="email"
                      className="px-4 w-full border-2 py-2 rounded-md text-sm outline-none mb-4"
                      placeholder="Enter your registered email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                  />
              )}

              <div className="flex justify-end space-x-3">
                  <button
                      className="bg-gray-400 text-white px-4 py-2 rounded"
                      onClick={onClose}
                      disabled={sendingOTP}
                  >
                      Cancel
                  </button>
                  <button
                      className={`${
                          sendingOTP ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
                      } text-white px-4 py-2 rounded`}
                      onClick={handleSendOTP}
                      disabled={sendingOTP}
                  >
                      {sendingOTP ? 'Sending...' : 'Send OTP'}
                  </button>
              </div>
          </div>
      </div>
  );
};
 
const Login = () => {
    const { loginHandler, loading, onGoogleSuccess } = useAuthContext();
    const [showPassword, setShowPassword] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [loginCredential, setLoginCredentials] = useState({
        email: "",
        password: "",
    });
 
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/dashboard";

    const [isAndroid, setIsAndroid] = useState(false);

    const scrollToTop = () =>
      window.scrollTo({ top: 0, behavior: "smooth" });
 
    useEffect(() => {
        const platform = Capacitor.getPlatform();
        if (platform === "android") {
            setIsAndroid(platform === 'android'); // Set isAndroid based on platform
            console.info("Ready for Android Google login using Browser API");
        }
    }, []);
 
    const handleSubmit = async (e) => {
        e.preventDefault();
        const result = await loginHandler(loginCredential);
        if (result.success) {
            toast.success(result.message);
            setTimeout(() => {
              navigate(from, { replace: true });
            }, 1000);
        } else {
            console.error("Login failed:", result.message);
        }
    };
 
   // const ClientId = "673274437090-f4fd0rh2t4b6evqi860c0bqhe0h2pqb2.apps.googleusercontent.com";
    const ClientId = "244583798442-pp0unolqbfnhrocpuce75tjr3fqkhpf3.apps.googleusercontent.com";
   // const androidClientId = '673274437090-0cs7k07v5dj4k00otr2m4erce3cq2rj2.apps.googleusercontent.com';
 
    // This function handles Google sign-in for Android using the Browser API
    const handleGoogleSignInAndroid = async () => {
        try {
            console.debug("Attempting Google Sign-In on Android...");
     
            const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${ClientId}&redirect_uri=${encodeURIComponent('https://digiuncle.co.in/auth/google/callback')}&scope=email%20profile`;
     
            // Open Google login inside WebView (InAppBrowser)
            const browser = InAppBrowser.create(googleAuthUrl, "_blank", "location=yes,hidden=no,toolbar=no");
     
            // Listen for redirects inside WebView
            browser.on("loadstop").subscribe(async (event) => {
                console.log("WebView Redirect URL:", event.url);
     
                // Ensure we're getting the redirect from Google OAuth
                if (event.url.startsWith("https://digiuncle.co.in/auth/google/callback?")) {
                    browser.close(); // Close WebView
     
                    // Extract the authorization code from URL
                    const urlParams = new URLSearchParams(new URL(event.url).search);
                    const code = urlParams.get("code");
     
                    if (code) {
                        console.debug("Google Auth Code:", code);
     
                        // Send the code to backend for token exchange
                        const result = await onGoogleSuccess({ credential: code });
     
                        if (result.success) {
                            toast.success(result.message);
                            navigate('/dashboard', { replace: true });
                        } else {
                            toast.error(result.message);
                        }
                    }
                }
            });
     
        } catch (error) {
            console.error("Google Sign-In Error:", error.message, error);
            toast.error("Google login failed: " + error.message);
        }
    };
   
    return (
        <>
           <Helmet>
  <title>Login – SomeOne4u</title>
  <meta name="description" content="Securely log in to your SomeOne4u account and continue your journey to meaningful connections across the globe." />
  <meta name="keywords" content="SomeOne4u login, dating site login, online dating access, member login, global dating account" />
  <meta name="author" content="SomeOne4u Team" />
  <meta name="application-name" content="SomeOne4u" />
  <link rel="canonical" href="https://someone4u.com/login" />
 
  {/* Open Graph */}
  <meta property="og:title" content="Login – SomeOne4u" />
  <meta property="og:description" content="Access your SomeOne4u account to discover meaningful connections with real singles near and far." />
  <meta property="og:url" content="https://someone4u.com/login" />
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="SomeOne4u" />
  <meta property="og:image" content="https://someone4u.com/image/splash.png" />
  <meta property="og:image:alt" content="SomeOne4u login graphic" />
 
  {/* Twitter Card */}
  {/* <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Login – SomeOne4u" />
  <meta name="twitter:description" content="Log in to SomeOne4u and explore new matches today!" />
  <meta name="twitter:image" content="https://someone4u.com/image/splash.png" /> */}
</Helmet>
 
          {/* Login Wrapper */}
          <div className="min-h-screen w-full font-sans text-gray-800 bg-gray-100 py-20 px-4 flex justify-center items-center">
            <div className="bg-white shadow-xl rounded-xl w-full max-w-md p-8 border-t-4 border-red-400">
              <h1 className="text-3xl font-extrabold text-center text-red-500 mb-6">Welcome Back to SomeOne4u</h1>
    
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    className="w-full border-2 border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your email"
                    onChange={(e) => setLoginCredentials({ ...loginCredential, email: e.target.value })}
                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                    required
                  />
                </div>
    
                <div className="relative">
                  <label className="block font-semibold text-gray-700 mb-1">Password</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full border-2 border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your password"
                    onChange={(e) => setLoginCredentials({ ...loginCredential, password: e.target.value })}
                    required
                  />
                  {showPassword ? (
                    <IoEyeOffOutline
                      className="absolute right-3 top-[60%] text-xl cursor-pointer text-gray-500"
                      onClick={() => setShowPassword(false)}
                    />
                  ) : (
                    <IoEyeOutline
                      className="absolute right-3 top-[60%] text-xl cursor-pointer text-gray-500"
                      onClick={() => setShowPassword(true)}
                    />
                  )}
                </div>
    
                <button
                  type="submit"
                  className={`w-full py-2 rounded font-semibold ${
                    loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                  } text-white transition-all duration-200`}
                  disabled={loading}
                >
                  {loading ? "Logging in..." : "Login"}
                </button>
              </form>
    
              <div className="flex justify-between mt-4 text-sm text-blue-600">
                <span className="cursor-pointer" onClick={() => setShowModal(true)}>
                  Forgot your password?
                </span>
                <span className="cursor-pointer"  onClick={() => {
                  navigate("/signup");
                  scrollToTop();
                }}>
                  Don’t have an account? Sign up
                </span>
              </div>
    
              {/* Google Login */}
              <div className="mt-6">
                {isAndroid ? (
                  <button
                    className="w-full bg-red-600 text-white py-2 rounded-md font-semibold"
                    onClick={handleGoogleSignInAndroid}
                  >
                    Sign In with Google
                  </button>
                ) : (
                  <GoogleOAuthProvider clientId={ClientId}>
                    <GoogleLogin
                      onSuccess={async (response) => {
                        const result = await onGoogleSuccess(response);
                        if (result.success) {
                          toast.success(result.message);
                          setTimeout(() => {
                            navigate('/dashboard', { replace: true });
                          }, 1000);
                        } else {
                          toast.error(result.message);
                        }
                      }}
                      onError={() => toast.error("Google login failed!")}
                    />
                  </GoogleOAuthProvider>
                )}
              </div>
            </div>
          </div>
    
          {/* Password Reset Modal */}
          <ForgotPasswordModal isVisible={showModal} onClose={() => setShowModal(false)} />
        </>
      );
    };    
 
export default Login;
